import { useEffect, useRef, useState } from 'react';
import { ReadyState } from 'react-use-websocket';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
  faPaperPlaneTop,
  faRotateRight,
  faStopCircle,
} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { Button, Icon, Render } from '@components/Shared';

const MAX_MESSAGE_LENGTH = 1000;

type Props = {
  value: string;
  isFullscreen: boolean;
  readyState: ReadyState;
  isDisabledSendButton: boolean;
  isWaitingResponse: boolean;
  clearError: () => void;
  reconnect: () => void;
  onValueChange: (value: string) => void;
  onMessage: (e: React.FormEvent) => void;
  error?: string | null;
};

export const ChatFooter = ({
  error,
  value,
  readyState,
  isFullscreen,
  isWaitingResponse,
  isDisabledSendButton,
  clearError,
  reconnect,
  onMessage,
  onValueChange,
}: Props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      onMessage(e);
    }
  };

  const handleTextareaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    onValueChange(event.target.value);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className="z-10 mx-auto flex w-full max-w-[768px] flex-col gap-5 px-6 pb-8 shadow-[0_-10px_10px_-2px_rgba(255,255,255,1)]">
      <Render if={error || readyState === ReadyState.CLOSED}>
        <div className="mx-auto flex w-full max-w-[420px] flex-col gap-3">
          <Render if={error}>
            <div className="animate-message-in rounded-lg bg-red-50 p-4 text-sm text-error-dark">
              <div className="flex items-center justify-between">
                <p>{error}</p>
                <Button
                  theme
                  aria-label="Close error message"
                  className="transition-opacity hover:opacity-60"
                  onClick={clearError}
                >
                  <Icon size="lg" icon={faXmark} className="rotate-180" />
                </Button>
              </div>
            </div>
          </Render>

          <Render if={readyState === ReadyState.CLOSED}>
            <div className="animate-message-in rounded-lg bg-neural-01 p-4 text-sm text-neural-05">
              <div className="flex items-center justify-between">
                <p>You are currently offline</p>
                <Button
                  theme
                  aria-label="Reconnect"
                  className="transition-opacity hover:opacity-60"
                  onClick={reconnect}
                >
                  <Icon size="lg" icon={faRotateRight} />
                </Button>
              </div>
            </div>
          </Render>
        </div>
      </Render>

      <form
        className={clsx(
          'relative flex items-start rounded-3xl border border-neural-03 transition-all',
          {
            'border-primary': isFocused,
            'shadow-sm': isFullscreen,
          },
        )}
        onSubmit={onMessage}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          name="message"
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder="Ask me something..."
          className={clsx(
            'w-full resize-none bg-transparent py-3 pl-5 pr-14 text-sm placeholder:text-neural-03',
            {
              'max-h-[72px]': !isFullscreen,
              'max-h-[120px]': isFullscreen,
            },
          )}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          onChange={handleTextareaChange}
        />

        <Button
          theme
          disabled={isDisabledSendButton}
          type="submit"
          className="absolute right-5 top-1/2 -translate-y-1/2"
        >
          <Icon
            size="lg"
            icon={isWaitingResponse ? faStopCircle : faPaperPlaneTop}
            className={clsx('text-neural-03 transition-colors', {
              'text-primary': isFocused && !isDisabledSendButton,
              'animate-pulse': isWaitingResponse,
            })}
          />
        </Button>

        <div className="absolute inset-x-0 top-full mt-1.5 flex justify-between text-[11px] text-neural-03">
          <span
            className={clsx('transition-opacity duration-300', {
              'opacity-0': value.length < 10,
            })}
          >
            <strong>Shift + Enter</strong> to add a new line
          </span>

          <span
            className={clsx('transition-all duration-300', {
              'opacity-0': value.length < 100,
              'text-error': value.length === MAX_MESSAGE_LENGTH,
            })}
          >
            {value.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </form>
    </div>
  );
};
